<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import type { QuerySpec } from "@yawhub/core";
import { api, errorText } from "../api";
import { filterDescription } from "../queryPresentation";
import ResultView from "./ResultView.vue";

const props = defineProps<{
  dataset: string;
  query: QuerySpec;
  group: (string | null)[];
  label: string;
  revision: string;
}>();
const emit = defineEmits<{ close: [] }>();
const heading = ref<HTMLHeadingElement>(),
  result = ref<any>(),
  busy = ref(false),
  error = ref(""),
  needsReload = ref(false);
let request = 0;
const fields = computed(() =>
  result.value?.schema.fields
    .filter(
      (f: any) =>
        f.active &&
        (!f.hidden ||
          props.query.dimensions.some((d) => d.fieldId === f.id) ||
          props.query.metrics.some((m) => m.fieldId === f.id)),
    )
    .map((f: any) => f.id),
);
const changed = computed(
  () =>
    result.value &&
    String(result.value.metadata.dataRevision) !== String(props.revision),
);
const filters = computed(() =>
  result.value
    ? props.query.filters
        .map((f) => filterDescription(f, result.value.schema.fields))
        .join(props.query.filterMode === "or" ? " eller " : " og ")
    : "",
);
async function load(append = false) {
  if (busy.value) return;
  const current = ++request;
  busy.value = true;
  error.value = "";
  try {
    const next = await api(`/api/datasets/${props.dataset}/query/drilldown`, {
      method: "POST",
      body: {
        query: props.query,
        group: props.group,
        limit: 50,
        ...(append ? { cursor: result.value?.nextCursor } : {}),
      },
    });
    if (current !== request) return;
    if (
      append &&
      String(next.metadata.dataRevision) !==
        String(result.value.metadata.dataRevision)
    ) {
      needsReload.value = true;
      error.value =
        "Data er ændret undervejs. Hent datagrundlaget igen for at se rækkerne samlet.";
      return;
    }
    result.value = append
      ? { ...next, rows: [...result.value.rows, ...next.rows] }
      : next;
    needsReload.value = false;
  } catch (e) {
    if (current === request) error.value = errorText(e);
  } finally {
    if (current === request) busy.value = false;
  }
}
onMounted(() => {
  heading.value?.focus();
  void load();
});
onUnmounted(() => {
  request++;
});
</script>

<template>
  <section class="result-drilldown" @keydown.esc.stop.prevent="emit('close')">
    <button type="button" class="text-button" @click="emit('close')">
      Tilbage til resultat
    </button>
    <h4 ref="heading" tabindex="-1">Datagrundlag: {{ label }}</h4>
    <p class="muted small">
      Samme filtre og periode som resultatet. Rækker uden en måling vises også.
    </p>
    <p v-if="query.period" class="muted small">
      Tidszone: {{ query.period.timezone }}
    </p>
    <details v-if="filters" class="source-filters">
      <summary>Se anvendte filtre</summary>
      <p>{{ filters }}</p>
    </details>
    <p v-if="changed" class="notice" role="status">
      Data er opdateret siden beregningen. Rækkerne viser det aktuelle
      datagrundlag.
    </p>
    <p v-if="error" class="error" role="alert">{{ error }}</p>
    <p v-if="busy && !result" class="muted" role="status">
      Henter datagrundlag…
    </p>
    <p v-if="result && !result.rows.length" class="empty-state">
      Ingen rækker matcher længere. Data kan være ændret siden beregningen.
    </p>
    <ResultView
      v-else-if="result"
      :result="result"
      :field-ids="fields"
      kind="table"
      show-updated-at
    />
    <div class="source-pagination">
      <p v-if="result" class="muted small" role="status">
        {{ result.rows.length }}
        {{ result.rows.length === 1 ? "række vist" : "rækker vist"
        }}{{
          result.nextCursor ? " · flere kan hentes" : " · alle rækker er hentet"
        }}
      </p>
      <button
        v-if="error"
        type="button"
        class="button secondary"
        :disabled="busy"
        @click="load(!!result && !needsReload)"
      >
        {{
          busy
            ? "Henter…"
            : needsReload
              ? "Hent datagrundlaget igen"
              : "Prøv igen"
        }}
      </button>
      <button
        v-else-if="result?.nextCursor"
        type="button"
        class="button secondary"
        :disabled="busy"
        @click="load(true)"
      >
        {{ busy ? "Henter…" : "Hent flere rækker" }}
      </button>
    </div>
  </section>
</template>

<style
  scoped
  lang="scss"
  src="../styles/components/ResultDrilldown.scss"
></style>
